import type { ChatEffort } from './chatEffort'
import type { ChatModelsConfig } from './chatSelection'

/**
 * The AI controls a send carries. Built by selectionPayload(), which omits a key
 * rather than sending null when the user has not chosen — the server then applies
 * its own default instead of interpreting an explicit null.
 *
 * `effort` covers reasoning entirely: 'none' means don't. There is no separate
 * `thinking` flag, because there was one and it fought with this field.
 */
export interface SelectionPayload {
  model?: string
  effort?: ChatEffort
  /** Auto-verify switch. Omitted until the user has touched it; the server defaults to on. */
  agentic?: boolean
}

const SERVER_AI_URL = import.meta.env.VITE_SERVER_AI_URL

async function api(action: string, payload?: any) {
  const res = await fetch(`${SERVER_AI_URL}/${action}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: payload && JSON.stringify(payload),
    credentials: 'include'
  }).then(res => res.json())

  if (import.meta.env.DEV) console.debug(`/ai/${action}:`, payload, res)

  if (res.status === 'ok') {
    return res.data
  } else {
    throw res
  }
}

/**
 * Coerce a list endpoint's payload into an array.
 *
 * `api()` hands back `res.data` untouched, so an `ok` envelope carrying no
 * `data` key resolves to `undefined` — while the declared return type promises
 * an array and every caller iterates or reads `.length` immediately. That gap is
 * not theoretical: it crashed BotLive with an uncaught "publishes is not
 * iterable" during render, which blanks the page rather than showing an empty
 * state, because `useMemo(() => buildGroups(publishes))` runs before anything
 * can guard it.
 *
 * An `ok` response with nothing in it means "no rows", so an empty array is the
 * honest reading. A genuine failure still throws from `api()` above and never
 * reaches here.
 */
function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

// Message status type (mirrors backend)
export type MessageStatus = 'in_progress' | 'processed' | 'failed' | 'canceled'

export interface Bot {
  bid: string
  title: string
  initialPrompt: string
  created: string
  updated: string
  state: number
}

export interface CreateBotResponse {
  bid: string
  title: string
}

export interface SendMessageResponse {
  msgId: number
}

export interface CancelMessageResponse {
  msgId: number
  status: MessageStatus
}

export interface RetryMessageResponse {
  assistantMsgId: number
}

export interface SubmitFormResponse {
  msgId: number
  status: MessageStatus
}

export interface DismissFormResponse {
  msgId: number
  status: MessageStatus
}

export interface BotCode {
  codeId: number
  cid: string
  version?: number
  code: string
  strategyDescription: string
  created: string
  isCurrent?: boolean
  isPublished?: boolean
}

export interface BotMessage {
  msgId: number
  role: 'user' | 'assistant' | 'tool' | 'chart_context'
  /**
   * Message body. For role 'chart_context' the server returns an already
   * parsed object ({ label, text }) instead of a string — do not JSON.parse.
   */
  content: string | { label: string; text: string }
  /**
   * The model's reasoning summary for an assistant turn, when the user asked for
   * thinking and the provider returned one. Absent — not null — on every other
   * row, so nothing has to distinguish "no reasoning" from "empty reasoning".
   */
  reasoning?: string
  status: MessageStatus
}

export interface BacktestResultData {
  strategy_name?: string
  symbol?: string
  total_trades?: number
  initial_balance?: number
  final_balance?: number
  total_return?: number
  return_percentage?: number
  winning_trades?: number
  losing_trades?: number
  win_rate?: number
  avg_win?: number
  avg_loss?: number
  profit_factor?: number
  sharpe_ratio?: number
  sortino_ratio?: number
  max_drawdown?: number
  max_drawdown_tick_level?: number
  total_commission_paid?: number
  commission_per_lot?: number
  slippage_pips?: number
  equity_curve_points?: number
  trades_capped?: boolean
  original_trade_count?: number
  // Gross P&L exposure
  gross_profit?: number
  gross_loss?: number
  // Per-trade extremes
  best_pnl?: number
  worst_pnl?: number
  // Long / short split
  long_count?: number
  short_count?: number
  long_wins?: number
  short_wins?: number
  long_losses?: number
  short_losses?: number
  long_gross_profit?: number
  long_gross_loss?: number
  short_gross_profit?: number
  short_gross_loss?: number
  long_net_pnl?: number
  short_net_pnl?: number
  long_win_rate?: number
  short_win_rate?: number
  avg_long_win?: number
  avg_long_loss?: number
  avg_short_win?: number
  avg_short_loss?: number
  long_profit_factor?: number
  short_profit_factor?: number
  // Streaks
  max_consec_wins?: number
  max_consec_losses?: number
  max_consec_profit?: number
  max_consec_loss?: number
  // Higher-level summary metrics
  avg_hold_minutes?: number
  expectancy?: number
  recovery_factor?: number
}

export interface BacktestResult {
  bid?: string
  btid: string
  version?: number
  cid?: string
  codeVersion?: number
  configInput: {
    strategy_config: Record<string, any>
    risk_management_config: Record<string, any>
    backtest_config: Record<string, any>
  }
  resultSummary: {
    status?: string
    data?: BacktestResultData
    error?: string
  } | null
  status: number
  progressBacktest?: string | null
  progressView?: string | null
  created: string
  updated: string
}

/**
 * The period a walkthrough actually runs, as the server resolved it: the drawn span padded
 * for indicator warm-up, snapped to whole UTC days (the engine takes date-only bounds), and
 * clamped to the datafeed's coverage.
 */
export interface WalkthroughPeriod {
  startDate: string   // 'YYYY-MM-DD', inclusive
  endDate: string     // 'YYYY-MM-DD', inclusive
  days: number
  bars: number
}

/**
 * A walkthrough run. Deliberately a superset of BacktestResult: it IS a backtest row, so
 * the whole replay stack (parseBacktestConfig, useBacktestChartState, fetchBacktestEvents)
 * drives it unchanged.
 */
export interface WalkthroughResult extends BacktestResult {
  walkthrough: {
    symbol: string
    timeframe: string
    period: WalkthroughPeriod
    requested?: { fromTs: number; toTs: number }
  }
  /** start(): true when an existing run was replayed instead of a new one queued. */
  reused?: boolean
  /** get(): whether the stored run still covers the supplied span. null when no span. */
  reusable?: boolean | null
}

/** The workbench's side of the contract: which chart, and the drawings' raw ms span. */
export interface WalkthroughSpan {
  symbol: string
  timeframe: string
  fromTs: number   // earliest drawing point, ms
  toTs: number     // latest drawing point, ms
}

export interface BacktestCodeResult {
  bid: string
  codeId: number
  cid: string
  version?: number
  code: string
  strategyDescription: string
  created: string
}

/** Summary row for backtests linked to a code version (by cid) */
export interface BacktestListItemByCode {
  btid: string
  status: number
  created: string
  version?: number
  symbol: string | null
  timeframe: string | null
  startDate: string | null
  endDate: string | null
}

export interface BacktestProgressResult {
  status: number
  progressBacktest: string | null
}

export interface BacktestTrade {
  dealId: number
  dealDatetime: string
  dealData: {
    type?: string
    symbol?: string
    entry_time?: string
    entry_price?: number
    exit_time?: string
    exit_price?: number
    pnl?: number
    volume?: number
    duration_minutes?: number
    balance?: number
    [key: string]: any
  } | null
}

export interface BacktestTradesResponse {
  trades: BacktestTrade[]
  total: number
  page: number
  pageSize: number
}

export interface BacktestGraphBucket {
  entries: number
  profit: number
  loss: number
  // Per-trading-session entry counts (Asia: h<8, Europe: 8..15, USA: 16+).
  // Optional for backwards compatibility with backtests produced before the
  // server started emitting this breakdown.
  sessions?: { asia: number; europe: number; usa: number }
}

export interface BacktestGraphData {
  hourly: Record<string, BacktestGraphBucket>
  weekdays: Record<string, BacktestGraphBucket>
  months: Record<string, BacktestGraphBucket>
  balance: Array<{ id: number; balance: number }>
  // Aggregations introduced for the new Results tab. Optional for backwards
  // compatibility with backtests that pre-date this server logic.
  monthly_pnl?: Array<{ label: string; profit: number; loss: number; cumulative: number }>
  day_of_week?: Array<{ day: string; profit: number; loss: number }>
  yearly_returns?: Array<{ year: number; months: Array<number | null> }>
  mfe_mae?: Array<{ trade: number; mfe: number; mae: number; avgProfit: number; avgLoss: number }>
}

export interface EquityPointRaw {
  time: string
  balance: number
  equity: number
  // Running aggregates emitted by the BacktestEngine; all optional so older
  // backtests (which only carry { time, balance, equity }) keep working.
  total_trades?: number
  winning_trades?: number
  losing_trades?: number
  gross_profit?: number
  gross_loss?: number
  total_commission_paid?: number
  max_drawdown_pct?: number
  best_pnl?: number
  worst_pnl?: number
  long_count?: number
  short_count?: number
  long_wins?: number
  short_wins?: number
}

export interface BacktestEventRaw {
  event_id: number
  event_time: string
  event_type: 'position_opened' | 'position_closed'
  payload: {
    entry_time?: string
    exit_time?: string
    type?: string
    entry_price?: number
    exit_price?: number
    volume?: number
    sl?: number
    tp?: number
    pnl?: number
    commission_cost?: number
    exit_reason?: string
    balance?: number
    mfe?: number
    mae?: number
  } | null
}

export interface BacktestClosedTradesResponse {
  events: BacktestEventRaw[]
  total: number
  page: number
  pageSize: number
}

export interface BacktestLogEntry {
  event_id: number
  event_time: string
  level: string
  message: string
}

export interface BacktestLogsResponse {
  events: BacktestLogEntry[]
  total: number
  page: number
  pageSize: number
}

export interface OptimizerResult {
  optid: string
  configInput: {
    optimization_config: Record<string, any>
    backtest_config?: {
      symbol: string
      timeframe: string
      start_date: string
      end_date: string
      initial_balance: number
    }
  }
  resultSummary: {
    status?: string
    data?: {
      stats?: {
        total_combinations?: number
        total_time_seconds?: number
        avg_time_per_backtest_seconds?: number
        optimize_for?: string
        mode?: string
      }
      winners?: Array<{
        rank: number
        params: Record<string, any>
        metrics: {
          return_percentage?: number
          sharpe_ratio?: number
          profit_factor?: number
          max_drawdown?: number
          win_rate?: number
          total_trades?: number
          final_balance?: number
          total_return?: number
          winning_trades?: number
          losing_trades?: number
          avg_win?: number
          avg_loss?: number
          max_drawdown_tick_level?: number
        }
        error?: string
      }>
    }
    error?: string
  } | null
  status: number
  created: string
  updated: string
}

export interface PublishResult {
  pcid: string
  title: string
  codeId: number
  cid: string
  version?: number
  configInput: {
    strategy_config: Record<string, any>
    risk_management_config: Record<string, any>
  }
  hasDraft?: boolean
  isConnected?: boolean
  heartbeatAt?: string | null
  status: number
  created: string
  updated: string
  botBid?: string
  botTitle?: string
}

export const botApi = {
  /**
   * Create a new bot
   */
  async create(
    initialPrompt: string,
    title?: string,
    selection?: SelectionPayload,
  ): Promise<CreateBotResponse> {
    return api('bot/new', { initialPrompt, title, ...selection })
  },

  /**
   * Create a new bot with a title only (no initial prompt, no AI kickoff)
   */
  async createEmpty(title: string): Promise<CreateBotResponse> {
    return api('bot/create', { title })
  },

  async list(): Promise<Bot[]> {
    return api('bot/list')
  },

  async get(bid: string): Promise<Bot> {
    return api('bot/get', { bid })
  },

  async update(bid: string, title: string): Promise<void> {
    return api('bot/update', { bid, title })
  },

  async getWorkbench(bid: string): Promise<{ state: Record<string, unknown> | null }> {
    return api('bot/workbench/get', { bid })
  },

  async saveWorkbench(bid: string, state: Record<string, unknown>): Promise<void> {
    return api('bot/workbench/save', { bid, state })
  },

  /**
   * Best-effort save that survives page unload (pagehide). The shared api()
   * helper can't set keepalive, so this builds the request directly.
   * Note: browsers cap keepalive bodies at ~64KB — larger states are dropped
   * silently; the debounced autosave bounds the loss window.
   */
  saveWorkbenchOnUnload(bid: string, state: Record<string, unknown>): void {
    try {
      void fetch(`${SERVER_AI_URL}/bot/workbench/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bid, state }),
        credentials: 'include',
        keepalive: true,
      })
    } catch {
      // page is going away — nothing useful to do
    }
  },

  async archive(bid: string): Promise<void> {
    return api('bot/archive', { bid })
  },

  async delete(bid: string): Promise<void> {
    return api('bot/delete', { bid })
  },

  async restore(bid: string): Promise<void> {
    return api('bot/restore', { bid })
  },

  async listArchived(): Promise<Bot[]> {
    return api('bot/list/archived')
  },

  async getMessages(bid: string): Promise<{ messages: BotMessage[] }> {
    return api('bot/messages', { bid })
  },

  /**
   * What the composer may offer: the selectable models, the plan's locks, and the
   * server-side defaults. Fetched once per chat mount.
   *
   * `pickerEnabled: false` still comes back with the FULL selectable list, and
   * the client still needs it: that is where the pinned default's effort list
   * comes from. The flag hides the model control (BotDetail) and is enforced
   * server-side (Subscription.normalizeSelection) — it does not trim this payload.
   *
   * This comment used to claim the list came back empty. It never did, and
   * believing it was how the effort control ended up offering the seven-tier
   * vocabulary instead of the pinned model's own.
   */
  async getModels(): Promise<ChatModelsConfig> {
    return api('bot/models')
  },

  async sendMessage(
    bid: string,
    prompt: string,
    chartContext?: { label: string; text: string },
    selection?: SelectionPayload,
  ): Promise<SendMessageResponse> {
    return api('bot/message', {
      bid,
      prompt,
      ...selection,
      ...(chartContext ? { chartContext } : {}),
    })
  },

  /**
   * Cancel the current in_progress message
   */
  async cancelMessage(bid: string): Promise<CancelMessageResponse> {
    return api('bot/message/cancel', { bid })
  },

  /**
   * Retry a failed message
   */
  async retryMessage(bid: string, selection?: SelectionPayload): Promise<RetryMessageResponse> {
    return api('bot/message/retry', { bid, ...selection })
  },

  /**
   * Submit form response
   */
  async submitFormResponse(bid: string, msgId: number, responseData: Record<string, any>, selection?: SelectionPayload): Promise<SubmitFormResponse> {
    return api('bot/form/submit', { bid, msgId, responseData, ...selection })
  },

  /**
   * Dismiss a pending clarifying-questions form without answering.
   * Unblocks the chat; triggers no AI continuation.
   */
  async dismissForm(bid: string, msgId: number): Promise<DismissFormResponse> {
    return api('bot/form/dismiss', { bid, msgId })
  },

  async getCurrentCode(bid: string): Promise<BotCode | null> {
    return api('bot/code/current', { bid })
  },

  async getCodeHistory(bid: string): Promise<BotCode[]> {
    return api('bot/code/history', { bid })
  },

  /** Load strategy code by external cid (same shape as backtest-linked code) */
  async getCodeByCid(cid: string): Promise<BacktestCodeResult | null> {
    return api('bot/code/get', { cid })
  },

  async getBacktestsByCid(cid: string): Promise<BacktestListItemByCode[]> {
    return api('bot/backtest/list-by-cid', { cid })
  },

  async getLatestBacktest(bid: string): Promise<BacktestResult | null> {
    return api('bot/backtest/latest', { bid })
  },

  async getBacktestByBtid(btid: string): Promise<BacktestResult | null> {
    return api('bot/backtest/get', { btid })
  },

  async getBacktestCode(btid: string): Promise<BacktestCodeResult | null> {
    return api('bot/backtest/code', { btid })
  },

  async getBacktestResults(btid: string): Promise<BacktestResultData | null> {
    return api('bot/backtest/results', { btid })
  },

  async getBacktestProgress(btid: string): Promise<BacktestProgressResult> {
    return api('bot/backtest/progress', { btid })
  },

  async updateProgressView(btid: string, progressView: string): Promise<void> {
    return api('bot/backtest/progress-view', { btid, progressView })
  },

  async getBacktestTrades(bid: string, btid: string, page: number = 1, pageSize: number = 25): Promise<BacktestTradesResponse> {
    return api('bot/backtest/trades', { bid, btid, page, pageSize })
  },

  async getBacktestGraphData(bid: string, btid: string): Promise<BacktestGraphData> {
    return api('bot/backtest/graph', { bid, btid })
  },

  async getBacktestEventsWindow(btid: string, startTime: string, endTime: string): Promise<BacktestEventRaw[]> {
    return api('bot/backtest/events/window', { btid, startTime, endTime })
  },

  async getBacktestClosedTrades(btid: string, page: number = 1, pageSize: number = 25): Promise<BacktestClosedTradesResponse> {
    return api('bot/backtest/closed-trades', { btid, page, pageSize })
  },

  async getBacktestLogs(btid: string, page: number = 1, pageSize: number = 50): Promise<BacktestLogsResponse> {
    return api('bot/backtest/logs', { btid, page, pageSize })
  },

  async getBacktestEquity(btid: string, afterTime?: string, beforeTime?: string): Promise<EquityPointRaw[]> {
    const payload: Record<string, string> = { btid }
    if (afterTime) payload.afterTime = afterTime
    if (beforeTime) payload.beforeTime = beforeTime
    return api('bot/backtest/equity', payload)
  },

  /**
   * Start — or attach to — the walkthrough for the bot's current code.
   *
   * Idempotent: called every time the user enters Walkthrough mode. The server replays the
   * stored run when the code, symbol, timeframe and covered period still match, and only
   * resolves a fresh config (one AI call, charged to the plan) when something material
   * changed. Returns with the row already queued; poll getBacktestProgress for the run.
   */
  async startWalkthrough(
    bid: string,
    span: WalkthroughSpan,
    selection?: SelectionPayload,
  ): Promise<WalkthroughResult> {
    return api('bot/walkthrough/start', { bid, ...span, ...selection })
  },

  /**
   * Read the current walkthrough without ever creating one. Passing a span also asks
   * whether that span is still covered (`reusable`), so the UI can tell in advance whether
   * entering the mode would rebuild.
   */
  async getWalkthrough(bid: string, span?: WalkthroughSpan): Promise<WalkthroughResult | null> {
    return api('bot/walkthrough/get', { bid, ...(span ?? {}) })
  },

  async getLatestOptimizer(bid: string): Promise<OptimizerResult | null> {
    return api('bot/optimizer/latest', { bid })
  },

  async publishBot(bid: string): Promise<{ pcid: string; title: string; status: number }> {
    return api('bot/publish', { bid })
  },

  async getPublishedBots(bid: string): Promise<PublishResult[]> {
    return asArray(await api('bot/publish/list', { bid }))
  },

  async getAllPublishedBots(): Promise<PublishResult[]> {
    return asArray(await api('bot/publish/list/all'))
  },

  async renamePublishedBot(pcid: string, title: string): Promise<{ pcid: string; title: string }> {
    return api('bot/publish/rename', { pcid, title })
  },

  async deletePublishedBot(pcid: string): Promise<{ pcid: string }> {
    return api('bot/publish/delete', { pcid })
  },
}
